import { createStyles, Divider, Paper, Skeleton } from '@mantine/core';
import { IconFilter, IconInfoCircle } from '@tabler/icons-react';
import { useRouter } from 'next/router';
import { useCallback, useContext, useMemo, useState } from 'react';

import repeat from '~/common/utils/repeat';
import { UserContext } from '~/components/providers/UserProvider';
import { Routes } from '~/domains/common/constants/routes';
import { ContainerNested } from '~/domains/design-system/Container';
import { Pill } from '~/domains/design-system/Pill';
import { useTutorialContext } from '~/domains/tutorials/TutorialModalContext';

import { reportQuickPicksFilters } from '../../../analyticsEvents';
import { useBoardContext } from '../../../context/BoardContext/BoardContext';

import dt from '~/testing';
import FilterLeague, { useStyles as useLeagueStyles } from './FilterLeague';
import FilterSearch from './FilterSearch';
import FilterType from './FilterType';
import { GameFilterModal } from './GameFilterModal';

const useStyles = createStyles((theme) => ({
  filters: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.other.spacing._8,
  },
  filtersLoadingContainer: {
    marginTop: theme.other.spacing._16,
  },
  secondRow: {
    display: 'flex',
    gap: theme.other.spacing._16,
    flexDirection: 'row-reverse',

    [theme.fn.smallerThan('md')]: {
      width: '100%',
      justifyContent: 'space-between',
    },

    '& .mantine-TextInput-root': {
      width: 300,

      [theme.fn.smallerThan('md')]: {
        maxWidth: '100%',
        width: '100%',
      },
    },
  },
  pill: {
    display: 'flex',
    flex: 1,
  },
  tutorialButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    borderRadius: theme.radius.md,
    padding: theme.other.spacing._6,
    height: 38,

    '@media (hover: hover)': {
      transition: 'opacity 150ms ease-in-out',
      '&:hover': {
        opacity: 0.7,
      },
    },
  },
  divider: {
    alignSelf: 'center',

    [theme.fn.largerThan('md')]: {
      display: 'none',
    },
  },
  gameFilterButton: {
    paddingLeft: theme.other.spacing._16,
    paddingRight: theme.other.spacing._16,
  },
  gameFilterButtonText: {
    [theme.fn.smallerThan('md')]: {
      display: 'none',
    },
  },
}));

function BoardFilters() {
  const { filters } = useBoardContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isUserSignedIn } = useContext(UserContext);
  const { classes, theme } = useStyles();
  const { classes: leagueClasses } = useLeagueStyles({ isBelowSubNav: isUserSignedIn });
  const { isLoading, data: filtersData } = filters.query;
  const router = useRouter();

  const { setIsOpen, tutorialSteps } = useTutorialContext();

  const handleTutorial = useCallback(() => {
    setIsOpen({
      isOpen: true,
      onClose: () => {
        setIsOpen({ isOpen: false });
      },
    });
  }, [setIsOpen]);

  const getLeagueFilters = useCallback(
    (leagueId: string) => filtersData?.filters?.leagues.find((league) => league.id === leagueId),
    [filtersData?.filters?.leagues]
  );

  const leagueFilters = useMemo(() => {
    if (!filtersData?.filters?.leagues) {
      return null;
    }

    return getLeagueFilters(filters.value.league);
  }, [filtersData?.filters?.leagues, getLeagueFilters, filters.value.league]);

  const typesOptions = useMemo(() => {
    if (!leagueFilters || leagueFilters.id === 'popular') {
      return [];
    }

    return leagueFilters.types.filter((type) => type.active) ?? [];
  }, [leagueFilters]);

  if (isLoading) {
    return (
      <>
        <ContainerNested className={leagueClasses.container}>
          {repeat(3, (i) => (
            <Skeleton key={i} height={34} style={{ borderRadius: '100em' }} width={70} />
          ))}
        </ContainerNested>
        <div className={classes.secondRow}>
          <Skeleton height={34} style={{ borderRadius: theme.other.spacing._8 }} width={283} />
          <Divider className={classes.divider} orientation="vertical" h={24} />
          <Skeleton height={34} style={{ borderRadius: '100em' }} width={48} />
        </div>
      </>
    );
  }

  if (!filtersData?.filters) {
    return null;
  }

  return (
    <>
      <FilterLeague
        options={filtersData.filters.leagues}
        value={filters.value.league}
        onChange={(leagueId) => {
          filters.set('league', leagueId);
          const newLeagueFilters = getLeagueFilters(leagueId);
          const activeType = newLeagueFilters?.types?.find((type) => type.active);
          const activeTypeId = activeType?.id ?? null;
          filters.set('type', activeTypeId);

          reportQuickPicksFilters({
            league: leagueId,
            type: activeTypeId,
          });
        }}
        isBelowSubNav={isUserSignedIn}
      />
      <div className={classes.secondRow}>
        {tutorialSteps?.length > 0 ? (
          <Paper onClick={handleTutorial} className={classes.tutorialButton}>
            <IconInfoCircle data-test-id={dt.contest.quickPicks.detail.general.iconInfoButton} />
          </Paper>
        ) : null}
        <FilterSearch
          onClick={() =>
            router.push(
              Routes.quickPicksBoardSearch({
                ...filters.value,
                league:
                  filters.value.league === filters.query.data.filters.leagues[0].id
                    ? undefined
                    : filters.value.league,
              })
            )
          }
        />
        {leagueFilters?.games?.length > 0 && (
          <>
            <Divider className={classes.divider} orientation="vertical" h={24} />
            <div className={classes.pill}>
              <Pill
                selected={!!filters.value.game?.length}
                onClick={() => setIsModalOpen(true)}
                className={classes.gameFilterButton}
              >
                <IconFilter size={16} />
              </Pill>
            </div>
          </>
        )}
      </div>
      {typesOptions.length > 0 && (
        <FilterType
          options={typesOptions}
          value={filters.value.type}
          onChange={(newValue) => {
            filters.set('type', newValue);
            reportQuickPicksFilters({
              league: filters.value.league,
              type: newValue,
              game: filters.value.game,
            });
          }}
          isBelowSubNav={isUserSignedIn}
        />
      )}
      <GameFilterModal isOpened={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}

export { BoardFilters };
